import { ConvertObjectCamelToSnakeCaseV2 } from "@/app/infrastructures/misc/common-library/ConvertObjectCamelToSnakeCase";
import {
  OptionsClass,
  QueryParamsEntitiesV3,
  TableErrorResponse
} from "./MainApp";
import { PaginationV2 } from "./Pagination";
import { DateRange } from "@/app/ui/views/dashboard/component/pos/components/type";

export class RevenueOverviewData {
  totalRevenue = 0;
  totalStt = 0;
  totalProfit = 0;
  earnedProfit = 0;
  pendingProfit = 0;

  constructor(fields?: Partial<RevenueOverviewData>) {
    Object.assign(this, fields);
  }
}

export class ChartPoint {
  date = "";
  totalRevenue = 0;
  totalProfit = 0;
  totalStt = 0;
  totalWeightKg = 0;
  revenuePercentageChange = 0;
  profitPercentageChange = 0;
  sttPercentageChange = 0;
  percentageChange = 0;
  
  constructor(fields?: Partial<ChartPoint>) {
    Object.assign(this, fields);
  }
}

export class BreakdownDataItem {
  value = 0;
  percentageChange = 0;

  constructor(fields?: Partial<BreakdownDataItem>) {
    Object.assign(this, fields);
  }
}

export class BreakdownItem {
  name = "";
  total = 0;
  percentageChange = 0;
  data: Record<string, BreakdownDataItem> = {};

  constructor(fields?: Partial<BreakdownItem>) {
    Object.assign(this, fields);

    // Convert nested raw objects to BreakdownDataItem
    if (fields?.data) {
      const newData: Record<string, BreakdownDataItem> = {};
      Object.entries(fields.data).forEach(([key, value]) => {
        newData[key] = new BreakdownDataItem(value as Partial<BreakdownDataItem>);
      });
      this.data = newData;
    }
  }
}

export class Breakdown {
  codTypes: BreakdownItem[] = [];
  channels: BreakdownItem[] = [];
  productTypes: BreakdownItem[] = [];
  destinationCities: BreakdownItem[] = [];
  commodities: BreakdownItem[] = [];

  constructor(fields?: Partial<Breakdown>) {
    Object.assign(this, fields);
  }
}

export class RevenueDashboardBreakdownChartDataItem {
  codTypes: RevenueDashboardBreakdownDataPerSection[] = [];
  channels: RevenueDashboardBreakdownDataPerSection[] = [];
  productTypes: RevenueDashboardBreakdownDataPerSection[] = [];
  destinationCities: RevenueDashboardBreakdownDataPerSection[] = [];
  commodities: RevenueDashboardBreakdownDataPerSection[] = [];
  constructor(fields?: Partial<RevenueDashboardBreakdownChartDataItem>) {
    Object.assign(this, fields);
  }
}
export class RevenueBreakdownData {
  chart: ChartPoint[] = [];
  comparisonChart: ChartPoint[] = [];
  breakdown: RevenueDashboardBreakdownChartDataItem = new RevenueDashboardBreakdownChartDataItem();
  lastSyncAt = "";

  constructor(fields?: Partial<RevenueBreakdownData>) {
    Object.assign(this, fields);
  }
}

export class PosRevenueOverview {
  overview = new RevenueOverviewData();
  comparison = new RevenueOverviewData();
  totalRevenueDifferencePercentage = 0;
  totalProfitDifferencePercentage = 0;
  totalSttDifferencePercentage = 0;

  constructor(fields?: Partial<PosRevenueOverview>) {
    Object.assign(this, fields);
  }
}

export class RevenueDashboardState {
  mainDateRange = "";
  compareDateRange = "";
  dataGrouping = "omzet";
  overviewData = new PosRevenueOverview();
  breakdownData = new RevenueBreakdownData();
  isLoadingOverview = false;
  isLoadingBreakdown = false;
  errorOverview = false;
  isErrorBreakdown = false;
  errorBreakdown = "";
  codTypes = "";
  channels = "";
  productTypes = "";
  destination3lc = "";
  commodityCodes = "";

  constructor(fields?: Partial<RevenueDashboardState>) {
    Object.assign(this, fields);
  }
}

export class RevenueBreakdownRequest {
  startDate?: string;
  endDate?: string;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  groupBy?: string;
  codTypes?: string;
  channels?: string;
  productTypes?: string;
  destination3lc?: string;
  commodityCodes?: string;

  constructor(fields?: Partial<RevenueBreakdownRequest>) {
    Object.assign(this, fields);
  }

  toQueryString(): string {
    return new QueryParamsEntitiesV3(
      ConvertObjectCamelToSnakeCaseV2({
        startDate: this.startDate,
        endDate: this.endDate,
        comparisonStartDate: this.comparisonStartDate,
        comparisonEndDate: this.comparisonEndDate,
        groupBy: this.groupBy,
        codTypes: this.codTypes,
        channels: this.channels,
        productTypes: this.productTypes,
        destination3lc: this.destination3lc,
        commodityCodes: this.commodityCodes
      })
    ).queryString;
  }
}

export class RevenueDashboardListRequest {
  groupBy = new OptionsClass();
  dates = new DateRange();

  constructor(fields?: Partial<RevenueDashboardListRequest>) {
    Object.assign(this, fields);
  }

  toQueryString(): string {
    return new QueryParamsEntitiesV3(
      ConvertObjectCamelToSnakeCaseV2({
        startDate: this.dates.main.start,
        endDate: this.dates.main.end,
        comparisonStartDate: this.dates.compare.start,
        comparisonEndDate: this.dates.compare.end,
        groupBy: this.groupBy.value
      })
    ).queryString;
  }
}

export class RevenueDashboardList {
  data: RevenueDashboardState[] = [];
  pagination = new PaginationV2();
  loading = false;
  error = new TableErrorResponse();
  params = new RevenueDashboardListRequest();

  constructor(fields?: Partial<RevenueDashboardList>) {
    Object.assign(this, fields);

    if (fields?.pagination) {
      this.pagination = new PaginationV2(fields.pagination);
    }

    if (fields?.data) {
      this.data = fields.data.map(
        (item: RevenueDashboardState) => new RevenueDashboardState(item)
      );
    }
  }
}
export class RevenueDashboardBreakdownChartData {
  date = "";
  totalRevenue = 0;
  totalProfit = 0;
  totalStt = 0;
  totalWeightKg = 0;
  revenuePercentageChange = 0;
  profitPercentageChange = 0;
  sttPercentageChange = 0;
  constructor(fields?: Partial<RevenueDashboardBreakdownChartData>) {
    Object.assign(this, fields);
  }
}
export class RevenueDashboardBreakdownDataPerSection {
  name = "";
  total = 0;
  percentageChange = 0;
  data = {};
  constructor(fields?: Partial<RevenueDashboardBreakdownDataPerSection>) {
    Object.assign(this, fields);
  }
}
export class RevenueDashboardBreakdownData {
  chart: RevenueDashboardBreakdownChartData[] = [];
  breakdown: RevenueDashboardBreakdownChartDataItem = new RevenueDashboardBreakdownChartDataItem();
  lastSyncAt = "";
  constructor(fields?: Partial<RevenueDashboardBreakdownData>) {
    Object.assign(this, fields);
  }
}
export class RevenueOverviewApiRequest {
  startDate = "";
  endDate = "";
  comparisonStartDate = "";
  comparisonEndDate = "";

  constructor(fields?: RevenueOverviewApiRequest) {
    Object.assign(this, fields);
  }

  toQueryString() {
    return new QueryParamsEntitiesV3(
      ConvertObjectCamelToSnakeCaseV2(this)
    ).queryString;
  }
}

export class RevenueBreakdownApiRequest {
  dataType = "";
  startDate = "";
  endDate = "";
  comparisonStartDate = "";
  comparisonEndDate = "";
  groupBy = "";
  codTypes = "";
  channels = "";
  productTypes = "";
  destination3lc = "";
  commodityCodes = "";

  constructor(fields?: RevenueBreakdownApiRequest) {
    Object.assign(this, fields);
  }

  toQueryString() {
    const snakeObj = ConvertObjectCamelToSnakeCaseV2(this);

    const patched = Object.fromEntries(
      Object.entries(snakeObj)
        .filter(([key]) => key !== "data_type")
        .map(([key, val]) => {
        const fixedKey = key.replace(/(\d+)/g, "_$1"); 
        return [fixedKey, val];
      })
    );

    return new QueryParamsEntitiesV3(patched).queryString;
  }
}

export class RevenueDashboardCustomerListRequest {
  page = 1;
  limit = 10;
  tag = "";
  customerName = "";
  sortBy = "";
  orderBy = "";
  constructor(fields?: RevenueDashboardCustomerListRequest) {
    Object.assign(this, fields);
  }

  toQueryString() {
    return new QueryParamsEntitiesV3(ConvertObjectCamelToSnakeCaseV2(this))
      .queryString;
  }
}

export class CustomerLeaderboardPerformancePeriode {
  month = "";
  amount = 0;
  weightKg = 0;
  growthPercentage = 0;
  stt = 0;
  constructor(fields?: Partial<CustomerLeaderboardPerformancePeriode>) {
    Object.assign(this, fields);
  }
}
export class CustomerLeaderboardPerformance {
  currentPeriode: CustomerLeaderboardPerformancePeriode = new CustomerLeaderboardPerformancePeriode();
  previousPeriode: CustomerLeaderboardPerformancePeriode[] = [];
  constructor(fields?: Partial<CustomerLeaderboardPerformance>) {
    Object.assign(this, fields);
  }
}

export class CustomerLeaderboardResponse {
  lastSyncAt = "";
  periodLabel = "";
  comparisonMonths: string[] = [];
  data: CustomerLeaderboard[] = [];
  constructor(fields?: Partial<CustomerLeaderboardResponse>) {
    Object.assign(this, fields);
  }
}

export class RevenueDashboardCustomerState {
  isLoadingCustomerLeaderboard = false;
  errorCustomerLeaderboard = false;
  customerLeaderboardData = new CustomerLeaderboardResponse();

  constructor(fields?: Partial<RevenueDashboardCustomerState>) {
    Object.assign(this, fields);
  }
}

export class CustomerLeaderboard {
  rank = 0;
  rankChange = 0;
  phoneNumber = "";
  name = "";
  lastBooking = "";
  tag: string[] = [];
  performance: CustomerLeaderboardPerformance = new CustomerLeaderboardPerformance();
  constructor(fields?: Partial<CustomerLeaderboard>) {
    Object.assign(this, fields);
  }
}
